import React from 'react';
import ReactDOM from 'react-dom';
import { ThumbsUp, Send, MessageCircle, Trash2, AlertTriangle } from 'lucide-react';
import { TravelProfile } from '../../../types/matching';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { createNotification } from '../../../lib/notification-service';
import * as amplitude from '@amplitude/analytics-browser';

interface ProfileCommentsProps {
  profile: TravelProfile;
  currentUserId: string;
  userInfo: any;
  isAdmin?: boolean;
}

export function ProfileComments({ profile, currentUserId, userInfo, isAdmin = false }: ProfileCommentsProps) {
  const [newComment, setNewComment] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [localComments, setLocalComments] = React.useState(profile.comments || []);
  const [commentToDelete, setCommentToDelete] = React.useState<string | null>(null);
  const commentRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    setLocalComments(profile.comments || []);
  }, [profile.comments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;
    if (!profile?.id) return;

    setIsSubmitting(true);
    amplitude.track('PVT Mate Post Commented');

    try {
      const comment = {
        id: Date.now().toString(),
        userId: currentUserId,
        username: userInfo?.user?.username || 'Anonyme',
        content: newComment.trim(),
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
        likes: []
      };

      const profileRef = doc(db, 'travel_profiles', profile.id);
      await updateDoc(profileRef, {
        comments: arrayUnion(comment)
      });

      setLocalComments(prevComments => [...prevComments, comment]);
      setNewComment('');

      createNotification({
        recipientId: profile.userId,
        senderId: currentUserId,
        senderUsername: userInfo?.user?.username || 'Anonyme',
        type: 'profile_comment',
        messageId: comment.id,
        messagePreview: comment.content,
        threadCode: profile.id,
        threadType: 'profile',
        profileId: profile.id,
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Erreur lors de l\'ajout du commentaire');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (commentId: string) => {
    try {
      if (!profile) return;
      const comment = localComments.find(c => c.id === commentId);
      if (!comment || comment.userId === currentUserId) return;

      const hasLiked = comment.likes.includes(currentUserId);
      const profileRef = doc(db, 'travel_profiles', profile.id);

      const updatedComments = localComments.map(c => {
        if (c.id === commentId) {
          return {
            ...c,
            likes: hasLiked
              ? c.likes.filter((id: string) => id !== currentUserId)
              : [...c.likes, currentUserId]
          };
        }
        return c;
      });

      setLocalComments(updatedComments);
      await updateDoc(profileRef, { comments: updatedComments });
    } catch (error) {
      console.error('Error updating like:', error);
      setLocalComments(profile.comments || []);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!isAdmin || !profile?.id) return;
    try {
      const updatedComments = localComments.filter(c => c.id !== commentId);
      setLocalComments(updatedComments);

      const profileRef = doc(db, 'travel_profiles', profile.id);
      await updateDoc(profileRef, { comments: updatedComments });
      setCommentToDelete(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
      setLocalComments(profile.comments || []);
      alert('Erreur lors de la suppression du commentaire');
    }
  };

  const getInitialColor = (index: number) => {
    const colors = [
      'bg-blue-500',
      'bg-teal-500',
      'bg-amber-500',
      'bg-rose-500',
      'bg-emerald-500',
      'bg-cyan-500',
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="pt-6 border-t border-gray-100 dark:border-gray-700/50">
      <div className="flex items-center gap-2.5 mb-5">
        <MessageCircle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Commentaires</h3>
        {localComments.length > 0 && (
          <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md">
            {localComments.length}
          </span>
        )}
      </div>

      <div className="space-y-3 mb-5">
        {localComments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-6 text-center">
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Aucun commentaire pour le moment.
            </p>
          </div>
        ) : (
          localComments.map((comment, index) => (
            <div
              key={comment.id}
              className="flex gap-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/50"
            >
              <div className={`w-8 h-8 rounded-full ${getInitialColor(index)} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                {comment.username[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {comment.username}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                      {format(comment.createdAt.toDate(), 'dd MMM yyyy', { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {isAdmin && (
                      <button
                        onClick={() => setCommentToDelete(comment.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {comment.userId !== currentUserId && (
                      <button
                        onClick={() => handleLike(comment.id)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                          comment.likes.includes(currentUserId)
                            ? 'bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400'
                            : 'text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20'
                        }`}
                      >
                        <ThumbsUp className={`w-3 h-3 ${comment.likes.includes(currentUserId) ? 'fill-current' : ''}`} />
                        {comment.likes.length > 0 && <span>{comment.likes.length}</span>}
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {comment.content}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <textarea
          ref={commentRef}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Partagez votre experience, vos conseils..."
          className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white resize-none text-sm transition-colors"
          rows={3}
        />
        <div className="flex justify-end mt-2.5">
          <button
            type="submit"
            disabled={!newComment.trim() || isSubmitting}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white font-medium rounded-xl transition-colors text-sm shadow-sm"
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            <span>Commenter</span>
          </button>
        </div>
      </form>

      {commentToDelete && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm ring-1 ring-black/5 dark:ring-white/5 animate-fade-in">
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Supprimer le commentaire
                </h3>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 ml-[52px]">
                Cette action est irreversible.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setCommentToDelete(null)}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDeleteComment(commentToDelete)}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
